import { useCallback, useRef, useState } from 'react';
import { WS_API_URL } from '../config';
import { savePlayerId } from '../lib/playerId';
import { useJsonSocket } from '../lib/useJsonSocket';
import {
  TRANSIENT_MATCH_FAILURES,
  type MatchDetails,
  type MatchmakingMessage,
  type MatchmakingRequest,
} from '../protocol';

interface Props {
  playerId: string;
  onPlayerIdChange: (id: string) => void;
  onMatchReady: (match: MatchDetails) => void;
}

type Tone = 'neutral' | 'good' | 'warn' | 'bad';

interface Status {
  text: string;
  tone: Tone;
}

const IDLE: Status = { text: '', tone: 'neutral' };

export default function MatchmakingScreen({ playerId, onPlayerIdChange, onMatchReady }: Props) {
  const [name, setName] = useState(playerId);
  const [searching, setSearching] = useState(false);
  const [status, setStatus] = useState<Status>(IDLE);

  // The name at the moment FIND MATCH was pressed — the socket URL and every
  // outgoing message must agree on it even if the input changes afterwards.
  const queuedNameRef = useRef(playerId);
  const [socketUrl, setSocketUrl] = useState<string | null>(null);

  const stopSearching = useCallback((text: string, tone: Tone) => {
    setSearching(false);
    setSocketUrl(null);
    setStatus({ text, tone });
  }, []);

  const handleOpen = useCallback(() => {
    setStatus({ text: 'Searching for an opponent…', tone: 'neutral' });
    send({ action: 'startMatchmaking', playerId: queuedNameRef.current });
  }, []); // `send` is created below and is referentially stable

  const handleMessage = useCallback(
    (msg: MatchmakingMessage) => {
      switch (msg.action) {
        case 'matchFound':
          setStatus({ text: 'Opponent found! Joining match…', tone: 'good' });
          send({ action: 'acceptMatch', playerId: queuedNameRef.current });
          break;

        case 'matchReady':
          setStatus({ text: 'Match ready! Launching game…', tone: 'good' });
          setSearching(false);
          close();
          onMatchReady({ dns: msg.dns, ip: msg.ip, port: msg.port, psess: msg.psess });
          break;

        case 'matchFailed':
          // A cancelled/expired ticket just means the queue moved on: get back
          // in line rather than dumping the player back on the idle screen.
          if (msg.reason && TRANSIENT_MATCH_FAILURES.has(msg.reason)) {
            setStatus({ text: 'Re-entering queue…', tone: 'warn' });
            send({ action: 'startMatchmaking', playerId: queuedNameRef.current });
          } else {
            stopSearching(`Match failed: ${msg.reason ?? 'Unknown error'}`, 'bad');
          }
          break;
      }
    },
    [onMatchReady, stopSearching], // `send`/`close` are referentially stable
  );

  const handleClose = useCallback(
    (reason: 'closed' | 'error') => {
      stopSearching(
        reason === 'error'
          ? 'Connection error — check your network.'
          : 'Connection closed unexpectedly.',
        'bad',
      );
    },
    [stopSearching],
  );

  const { send, close } = useJsonSocket<MatchmakingMessage, MatchmakingRequest>({
    url: socketUrl,
    onOpen: handleOpen,
    onMessage: handleMessage,
    onClose: handleClose,
  });

  const startMatchmaking = () => {
    if (searching) return;
    const trimmed = name.trim() || playerId;
    setName(trimmed);
    savePlayerId(trimmed);
    onPlayerIdChange(trimmed);
    queuedNameRef.current = trimmed;

    setSearching(true);
    setStatus({ text: 'Connecting to matchmaking server…', tone: 'neutral' });
    setSocketUrl(`${WS_API_URL}?playerId=${encodeURIComponent(trimmed)}`);
  };

  return (
    <main className="screen screen--center">
      <header className="brand">
        <h1 className="brand__title">ROCK PAPER SCISSORS</h1>
        <p className="brand__subtitle">online</p>
      </header>

      <section className="panel">
        <label className="panel__label" htmlFor="player-name">
          Your Player Name
        </label>
        <input
          id="player-name"
          className="name-input"
          type="text"
          value={name}
          maxLength={24}
          spellCheck={false}
          autoComplete="off"
          disabled={searching}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => {
            const trimmed = name.trim();
            if (!trimmed) {
              setName(playerId);
              return;
            }
            setName(trimmed);
            savePlayerId(trimmed);
            onPlayerIdChange(trimmed);
          }}
        />
        <p className="panel__hint">Click to set a custom name</p>
      </section>

      <button
        type="button"
        className="btn btn--primary"
        onClick={startMatchmaking}
        disabled={searching}
      >
        {searching ? 'SEARCHING…' : 'FIND MATCH'}
      </button>

      <p className={`status status--${status.tone}`} role="status">
        {status.text}
      </p>

      {searching && <div className="spinner" aria-hidden="true" />}
    </main>
  );
}
